import React, { useEffect, useState } from 'react';

// COMPONENTS
import Box from '@/main/components/shared/box';
import Button from '@/main/components/shared/button/button';
import { AreaButton } from '@/main/components/shared/styles';
import KogInputTextControlledNew from '@/main/components/shared/inputText/inputTextControlledNew';
import KogAutoCompleteControlled from '@/main/components/shared/inputAutoComplete/autoCompleteControlled';
import SecondaryButton from '@/main/components/shared/button/secondaryButton';

// REDUX/UTILS/HOOKS
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import useFormQueryParams from '@/main/hooks/useformQueryParams';
import {
  getDefaultCountryOptionDrop,
  getDefaultOptionDrop,
  getTranslateByKey,
  isNullUndefined,
  isValueQueryParams,
  mountReducerWithData,
  removeEmptyValues
} from '@/main/services/utils';
import { getCountriesUser, useCountryReducer } from '@/main/store/country';
import { KeyValue } from '@/main/models/common';
import {
  getCompany,
  resetReducerCompany,
  useCompanyReducer
} from '@/main/store/company';
import {
  getCompanyType,
  useCompanyTypeReducer
} from '@/main/store/companyType';
import { getOptionsStatus } from '@/main/services/data/data';
import { useUserReducer } from '@/main/store/user';
import { userIsAdmin } from '@/main/services/utilsUser';
import { CompanyTypeEnum } from '@/main/services/enum/CompanyTypeEnum';
import { HeadQuarterBranchFilterEnum } from '@/main/services/enum/HeadQuarterBranchFilterEnum';
import { StatusCompanyEnum } from '@/main/services/enum/StatusCompanyEnum';
import { StatusRegisterEnum } from '@/main/services/enum/StatusRegisterEnum';
import { RoutesConstants } from '@/main/routes/routesConstants';
import {
  CompanyFilterRequest,
  ReportTemplateFilterRequest
} from '@/main/models/request';

import * as S from './styles';

interface IFilterReportTemplate {
  showFilters: boolean;
  setShowFilters: React.Dispatch<React.SetStateAction<boolean>>;
  onResetSearch: () => void;
  getDataReportTemplate: (filters: ReportTemplateFilterRequest) => void;
}

function FiltersReportTemplate({
  showFilters,
  setShowFilters,
  onResetSearch,
  getDataReportTemplate
}: IFilterReportTemplate) {
  //#region Hooks
  const dispatch = useDispatch();
  //#endregion

  //#region Reducers
  const { currentUser: userReducer } = useUserReducer();
  const { countriesUser: countryReducer } = useCountryReducer();
  const { company: companyReducer } = useCompanyReducer();
  const { companyType: companyTypeReducer } = useCompanyTypeReducer();
  //#endregion

  //#region States
  const typeCompanyUser = !userIsAdmin()
    ? String(userReducer?.idCompanyType)
    : null;

  const optionsStatus = getOptionsStatus();

  const optionDefaultCountryDrop = getDefaultCountryOptionDrop(
    countryReducer.data
  );
  const optionDefaultCompanyTypeDrop = getDefaultOptionDrop(
    companyTypeReducer.data,
    typeCompanyUser
  );
  const optionDefaultCompanyDrop = getDefaultOptionDrop(companyReducer.data);
  const optionDefaultStatus = optionsStatus.find(
    item => item.key === StatusRegisterEnum.Active
  )!;

  const INITIAL_FORM_STATE = {
    country: null as KeyValue | null,
    reportTemplateName: '',
    company: null as KeyValue | null,
    companyType: null as KeyValue | null,
    statusRegister: null as KeyValue | null
  };
  type ISearchReportTemplateForm = typeof INITIAL_FORM_STATE;

  const methods = useFormQueryParams<ISearchReportTemplateForm>({
    mode: 'onChange',
    defaultValues: INITIAL_FORM_STATE,
    mapDefaultValues: {
      country: countryReducer,
      company: companyReducer,
      companyType: companyTypeReducer,
      statusRegister: mountReducerWithData(optionsStatus)
    },
    onSubmit: onSubmit
  });
  const {
    control,
    setValue,
    getValues,
    formState: { errors },
    queryParamsValues,
    clearErrors,
    reset,
    customHandleSubmit
  } = methods;
  //#endregion

  //#region Core functions
  function handleClear() {
    onResetSearch();
    reset(INITIAL_FORM_STATE);
    setValue('country', optionDefaultCountryDrop);
    setValue('statusRegister', optionDefaultStatus);

    clearErrors();

    if (optionDefaultCountryDrop !== null) {
      setValue('country', optionDefaultCountryDrop);

      if (optionDefaultCompanyTypeDrop !== null) {
        setValue('companyType', optionDefaultCompanyTypeDrop);

        if (optionDefaultCompanyDrop !== null)
          setValue('company', optionDefaultCompanyDrop);
      }
    } else {
      dispatch(resetReducerCompany());
    }
  }

  function onSubmit(formInputs: ISearchReportTemplateForm) {
    const request: ReportTemplateFilterRequest = {
      guidCountry: String(formInputs.country?.key),
      guidCompany: String(formInputs.company?.key),
      companyType: formInputs.companyType?.key
        ? Number(formInputs.companyType.key)
        : null,
      statusRegister: formInputs.statusRegister?.key
        ? Number(formInputs.statusRegister.key)
        : null,
      reportName: formInputs.reportTemplateName
    };

    getDataReportTemplate(removeEmptyValues(request));
    setShowFilters(false);
  }
  //#endregion

  // #region Async functions
  function callCompanyApiDomain(
    countrySelected: KeyValue | null,
    companyTypeSelected: KeyValue | null
  ) {
    if (
      !isValueQueryParams(countrySelected) ||
      !isValueQueryParams(companyTypeSelected)
    ) {
      setValue('company', null);
    }

    dispatch(resetReducerCompany());

    if (countrySelected !== null && companyTypeSelected !== null) {
      const request: CompanyFilterRequest = {
        guidCountry: String(countrySelected.key),
        type:
          Number(companyTypeSelected.key) === CompanyTypeEnum.BodyShop
            ? HeadQuarterBranchFilterEnum.Only_Headquarters
            : HeadQuarterBranchFilterEnum.All,
        idStatusRegister: StatusCompanyEnum.Active,
        idCompanyType: String(companyTypeSelected.key)
      };
      dispatch(getCompany(request));
    }
  }
  //#endregion

  //#region UseEffects
  useEffect(() => {
    if (isNullUndefined(countryReducer.data)) dispatch(getCountriesUser());
    if (isNullUndefined(companyTypeReducer.data)) dispatch(getCompanyType());

    return () => {
      dispatch(resetReducerCompany());
    };
  }, []);

  useEffect(() => {
    // Call API's related by default country
    if (optionDefaultCountryDrop && optionDefaultCompanyTypeDrop) {
      callCompanyApiDomain(
        optionDefaultCountryDrop,
        optionDefaultCompanyTypeDrop
      );
    }
  }, [optionDefaultCountryDrop, optionDefaultCompanyTypeDrop]);

  useEffect(() => {
    const mountCountryQueryParams = isNullUndefined(queryParamsValues.country)
      ? null
      : queryParamsValues.country;
    const mountCompanyTypeQueryParams = isNullUndefined(
      queryParamsValues.companyType
    )
      ? null
      : queryParamsValues.companyType;

    if (!isNullUndefined(mountCompanyTypeQueryParams))
      callCompanyApiDomain(
        mountCountryQueryParams,
        mountCompanyTypeQueryParams
      );
  }, [JSON.stringify(queryParamsValues)]);
  //#endregion

  //#region Render constants
  const ID_SCREEN = 'id-searchReportTemplate';
  const generateIdScreen = (id: string) => `${ID_SCREEN}-${id}`;
  const hasLoadingReducers = countryReducer.isLoading;

  const isDisabledCompanyTypeDrop = () =>
    companyTypeReducer.data === null || optionDefaultCompanyTypeDrop !== null;

  const isDisabledCompanyDrop = () =>
    companyReducer.data === null ||
    getValues().companyType === null ||
    optionDefaultCompanyDrop !== null;
  //#endregion

  return (
    <Box showBox={showFilters} title={getTranslateByKey('information.filters')}>
      <form onSubmit={customHandleSubmit} noValidate>
        <S.RowInputs>
          <KogAutoCompleteControlled
            required
            id={generateIdScreen('select-country')}
            nameCustom="country"
            control={control}
            label={getTranslateByKey('information.country')}
            options={countryReducer.data}
            isLoading={countryReducer.isLoading}
            rulesCustom={{ required: true }}
            errorMessageCustom={
              errors.country?.type === 'required'
                ? getTranslateByKey('information.countryRequired', true)
                : ''
            }
            hasErrorCustom={!!errors.country}
            onChangeCustom={(optionSelected: KeyValue | null) => {}}
            valueDefaultKey={optionDefaultCountryDrop?.key}
            setValueCustom={setValue}
            disabled={optionDefaultCountryDrop !== null}
          />

          <KogAutoCompleteControlled
            id={generateIdScreen('select-companyType')}
            nameCustom="companyType"
            control={control}
            label={getTranslateByKey('information.companyType')}
            options={companyTypeReducer.data}
            isLoading={companyTypeReducer.isLoading}
            onChangeCustom={(optionSelected: KeyValue | null) => {
              callCompanyApiDomain(getValues().country, optionSelected);
            }}
            disabled={isDisabledCompanyTypeDrop()}
            valueDefaultKey={optionDefaultCompanyTypeDrop?.key}
            setValueCustom={setValue}
          />

          <KogAutoCompleteControlled
            id={generateIdScreen('select-company')}
            label={getTranslateByKey('control.company')}
            isDefaultValueUnique
            control={control}
            nameCustom="company"
            rulesCustom={{ required: false }}
            errorMessageCustom=""
            hasErrorCustom={false}
            options={companyReducer.data}
            isLoading={companyReducer.isLoading}
            onChangeCustom={(optionSelected: KeyValue | null) => {}}
            disabled={isDisabledCompanyDrop()}
            valueDefaultKey={optionDefaultCompanyDrop?.key}
            setValueCustom={setValue}
          />
        </S.RowInputs>

        <S.RowInputs>
          <KogInputTextControlledNew
            id={generateIdScreen('txt-report-name')}
            label={getTranslateByKey('information.reportTemplateName')}
            name="reportTemplateName"
            error={!!errors.reportTemplateName}
            control={control}
          />
          <KogAutoCompleteControlled
            id={generateIdScreen('select-status')}
            label={getTranslateByKey('information.status')}
            nameCustom="statusRegister"
            valueDefaultKey={String(StatusRegisterEnum.Active)}
            control={control}
            setValueCustom={setValue}
            options={optionsStatus}
            onChangeCustom={() => {}}
            rulesCustom={{ required: true }}
            hasErrorCustom={!!errors.statusRegister}
            errorMessageCustom={
              errors.statusRegister?.type === 'required'
                ? getTranslateByKey('information.statusRequired', true)
                : ''
            }
          />
        </S.RowInputs>

        <S.AreaButtons>
          <SecondaryButton
            id={generateIdScreen('btn-submit')}
            disabled={hasLoadingReducers}
            type="submit">
            {getTranslateByKey('information.search')}
          </SecondaryButton>
          <SecondaryButton
            id={generateIdScreen('btn-clear')}
            type="button"
            onClick={handleClear}>
            {getTranslateByKey('information.clear')}
          </SecondaryButton>
        </S.AreaButtons>
      </form>
    </Box>
  );
}

function FiltersReportTemplateWrapper(
  props: Omit<IFilterReportTemplate, 'showFilters' | 'setShowFilters'>
) {
  //#region States
  const [showFilters, setShowFilters] = useState<boolean>(true);
  //#endregion

  //#region hooks
  const navigate = useNavigate();
  //#endregion

  //#region HandleFunctions
  function handleFilters() {
    setShowFilters(prev => !prev);
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  }

  function handleNew() {
    navigate(RoutesConstants.registrationRegisterReportTemplate);
  }
  //#endregion

  const mergedProps: IFilterReportTemplate = {
    ...props,
    showFilters,
    setShowFilters
  };

  return (
    <>
      <AreaButton>
        <Button
          onClick={handleFilters}
          id="id-searchReportTemplate-btn-filters">
          {getTranslateByKey('information.filters')}
        </Button>
        <Button
          onClick={handleNew}
          id="id-searchReportTemplate-btn-newReportTemplate">
          {getTranslateByKey('information.new')}
        </Button>
      </AreaButton>
      <FiltersReportTemplate {...mergedProps} />
    </>
  );
}

export default FiltersReportTemplateWrapper;
